import type { EditAssetWorkflowOptions } from '../../types';

export const editTenantWorkflow: EditAssetWorkflowOptions = {
  workflowKey: 'editTenantWorkflow',
  workflowType: 'editAssetWorkflow',
  assetType: 'tenant',
  workflowIndex: 1,
  workflowName: 'Edit Tenant Workflow',
  workflowDescription: 'Edit a tenant',
  workflowStateTransformFunction: (assetPayload: any) => {
    const { originInfoMap } = assetPayload;

    let modifiedToMap: Record<string, any> | undefined;
    if (originInfoMap) {
      modifiedToMap = {};
      for (const configMapEntry of originInfoMap) {
        modifiedToMap[configMapEntry.originId] = configMapEntry;
      }
    }

    return {
      ...assetPayload,
      originInfoMap: modifiedToMap,
    };
  },
  workflowStepOptions: [
    {
      workflowStepKey: 'partnerProvidedId',
      workflowStepNavigationName: 'Tenant ID Alias',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Enter a unique identifier for this tenant',
        description:
          'This is optional but will allow you to more easily reference this tenant in carrier selection calls',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'partnerProvidedId',
      },
    },
    {
      workflowStepKey: 'name',
      workflowStepNavigationName: 'Name',
      workflowComponent: {
        workflowComponentType: 'assetNameEntry',
        prompt: 'Enter a name for this tenant',
        description: 'How will you refer to this tenant',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'name',
      },
    },
    {
      workflowStepKey: 'description',
      workflowStepNavigationName: 'Description',
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Enter a description of this tenant',
        description: '',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'description',
      },
    },
    {
      workflowStepKey: 'taxIdentificationNumber',
      workflowStepNavigationName: 'EIN Tax Identification #',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: `Enter this tenant's EIN tax identification number(required to ship US exports)`,
        description: '',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'taxIdentificationNumber',
      },
    },
    {
      workflowStepKey: 'tenantAddress',
      workflowStepNavigationName: 'Tenant Address',
      workflowComponent: {
        workflowComponentType: 'addressEntry',
        description: 'Select or create an address for this tenant',
        prompt: '',
        help: '',
      },
      assetToWorkflowStateMapping: {
        assetProperty: 'unitedStatesPrincipalPartyInInterestAddressId',
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'address',
        linkAssetTypeOverride: 'usppiAddresses',
      },
    },
    {
      workflowStepKey: 'tenantReturnAddressQuestion',
      workflowStepNavigationName: 'Return Address Overrides',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        defaultValue: '',
        prompt: 'Do you have any tenant return address overrides?',
        description: `When present, a tenant return address will override the origin return address.`,
        help: ``,
        options: [
          { optionText: 'Yes', optionValue: true },
          { optionText: 'No', optionValue: false },
        ],
      },
      assetToWorkflowStateMapping: {
        assetProperty: 'originInfoMap',
        computeFn: function (assetProperty) {
          return !!Object.values(assetProperty)?.length;
        },
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
    {
      workflowStepKey: 'tenantReturnAddress',
      workflowStepNavigationName: 'Tenant Return Addresses',
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'tenantReturnAddressQuestion',
          schema: { type: 'boolean', const: true },
        },
      ],
      workflowComponent: {
        workflowComponentType: 'tenantReturnAddressEntry',
        description: 'Select or create a return address for this tenant',
        prompt: 'Please configure tenant return address overrides for each origin.',
        help: 'RA: Return Address is the address where a package will be returned if undeliverable.  This address prints on the upper left corner of the shipping label.',
      },
      assetToWorkflowStateMapping: {
        assetProperty: 'originInfoMap',
        computeFn: function (assetProperty) {
          return Object.values(assetProperty);
        },
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'originInfoMap',
      },
    },
    {
      workflowStepKey: 'tenantContact',
      workflowStepNavigationName: 'Tenant Contact',
      workflowComponent: {
        workflowComponentType: 'contactEntry',
        description: 'Select or create a contact for this tenant',
        prompt: '',
        help: '',
      },
      assetToWorkflowStateMapping: {
        assetProperty: 'unitedStatesPrincipalPartyInInterestContactId',
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'contact',
        linkAssetTypeOverride: 'usppiContacts',
      },
    },
    {
      workflowStepKey: 'summary',
      workflowStepNavigationName: 'Configuration Summary',
      workflowComponent: {
        workflowComponentType: 'workflowSummary',
        title: 'Edit Tenant Summary',
        description: 'The tenant will be created.  Please review your selections.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
  ],
};
